// <copyright file="ComponentMenu.tsx">
// © 2023 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { Panel } from "primereact/panel";
import { Ripple } from "primereact/ripple";
import React from "react";
import styles from "src/components/ComponentMenu/ComponentMenu.module.css";
import type { MenuItem } from "primereact/menuitem";
import type { PanelHeaderTemplateOptions } from "primereact/panel";
import type { FC } from "react";

interface ComponentMenuProps {
    focusRef: React.Ref<Menu>;
    title: string;
    menuItems: MenuItem[];
}

/**
 * Menu for a main UI component, displayed in a collapsible left side bar.
 * @param props Component properties
 * @returns Menu component for a main UI component
 */
const ComponentMenu: FC<ComponentMenuProps> = (props: ComponentMenuProps) => {
    const template = (options: PanelHeaderTemplateOptions) => {
        const toggleIcon = options.collapsed
            ? "pi pi-angle-double-right"
            : "pi pi-angle-double-left";
        const headerClassName = options.className;
        return (
            <div className={headerClassName} style={{ padding: "2px 2px" }}>
                <Button
                    title="Expand/collapse component menu"
                    aria-controls="component-menu-panel"
                    icon={toggleIcon}
                    className="p-button p-button-text p-button-icon-only custom-focus-highlight"
                    onClick={options.onTogglerClick}
                />
                <Ripple />
            </div>
        );
    };

    const className = `${styles.ComponentMenu}`;
    return (
        <div
            aria-label="Component menu"
            role="navigation"
            className={className}
            data-testid="ComponentMenu"
        >
            <Panel
                id="component-menu-panel"
                toggleable
                headerTemplate={template}
            >
                {props.title}
                <Menu
                    ref={props.focusRef}
                    aria-haspopup={false}
                    model={props.menuItems}
                />
            </Panel>
        </div>
    );
};

export default ComponentMenu;
